// import { Injectable } from "@angular/core";
// import { ServerCommunicatorService } from "../services/server-communicator.service";
// import { TranslateTextRequest } from "../classes/infrastructure/request_response/translatetextrequest";
// import { UIUtils } from "./uiutils.service";

// export type TranslateLang = "en-to-mr" | "mr-to-en";

// @Injectable({ providedIn: "root" })
// export class TextTranslationUtilService {
//   constructor(
//     private communicator: ServerCommunicatorService,
//     private uiUtils: UIUtils
//   ) {}

//   // ─── Bilingual error messages (defined once, used for every field) ──────────
//   private readonly ERROR_TITLE    = { en: "Error",                              mr: "त्रुटी" };
//   private readonly ERROR_BODY_API = { en: "Translation failed. Please try again.", mr: "भाषांतर अयशस्वी झाले. कृपया पुन्हा प्रयत्न करा." };

//   private showError(serverMessage?: string): void {
//     const body = serverMessage
//       ? { en: serverMessage, mr: serverMessage } // server message shown as-is
//       : this.ERROR_BODY_API;                      // generic bilingual fallback

//     this.uiUtils.showErrorMessagelang(
//       this.getLangMsg(this.ERROR_TITLE),
//       this.getLangMsg(body)
//     );
//   }

//   private getLangMsg(pair: { en: string; mr: string }): string {
//     const lang = (localStorage.getItem("app_lang") as "en" | "mr") || "en";
//     return lang === "mr" ? pair.mr : pair.en;
//   }

//   // ─── Public API ─────────────────────────────────────────────────────────────

//   /**
//    * Translate a single string between English and Marathi.
//    * Errors (API failure or network) are shown automatically as bilingual alerts.
//    *
//    * @param text      The value to translate
//    * @param direction 'en-to-mr' or 'mr-to-en'
//    * @returns         Translated string, or empty string if translation fails
//    *
//    * @example
//    *   this.BirthRegisterEntity.p.FirstNameMr =
//    *     await this.translationUtil.translate(value, 'en-to-mr');
//    */
//   async translate(text: string, direction: TranslateLang): Promise<string> {
//     const value = text?.trim();
//     if (!value) return "";

//     const req = new TranslateTextRequest();
//     req.Text         = `-${value}-`;
//     req.FromLanguage = direction === "en-to-mr" ? 100 : 200;
//     req.ToLanguage   = direction === "en-to-mr" ? 200 : 100;

//     try {
//       const response = await this.communicator.GetTransaltedText(req);

//       if (response.Successful) {
//         // ✅ Success
//         return (response.ObjectTag?.ConvertedText ?? "")
//           .replace(/^-|-$/g, "")
//           .trim();
//       }

//       // ❌ API returned Successful=false — show server's message
//       this.showError(response.Message);
//       return "";

//     } catch {
//       // ❌ Network / runtime error — show generic bilingual message
//       this.showError();
//       return "";
//     }
//   }
// }





// With first letter capitalization and text language service //

import { Injectable } from "@angular/core";
import { ServerCommunicatorService } from "../services/server-communicator.service";
import { TranslateTextRequest } from "../classes/infrastructure/request_response/translatetextrequest";
import { UIUtils } from "./uiutils.service";

export type TranslateLang = "en-to-mr" | "mr-to-en";

@Injectable({ providedIn: "root" })
export class TextTranslationUtilService {
  constructor(
    private communicator: ServerCommunicatorService,
    private uiUtils: UIUtils
  ) {}

  // ─── Bilingual error messages (defined once, used for every field) ──────────
  private readonly ERROR_TITLE    = { en: "Error",                               mr: "त्रुटी" };
  private readonly ERROR_BODY_API = { en: "Translation failed. Please try again.", mr: "भाषांतर अयशस्वी झाले. कृपया पुन्हा प्रयत्न करा." };

  private showError(serverMessage?: string): void {
    const body = serverMessage
      ? { en: serverMessage, mr: serverMessage }
      : this.ERROR_BODY_API;

    this.uiUtils.showErrorMessagelang(
      this.getLangMsg(this.ERROR_TITLE),
      this.getLangMsg(body)
    );
  }

  private getLangMsg(pair: { en: string; mr: string }): string {
    const lang = (localStorage.getItem("app_lang") as "en" | "mr") || "en";
    return lang === "mr" ? pair.mr : pair.en;
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Translate a single string between English and Marathi.
   * Errors (API failure or network) are shown automatically as bilingual alerts.
   *
   * @param text       The value to translate
   * @param direction  'en-to-mr' or 'mr-to-en'
   * @param capitalize Capitalise the first letter of the result (default: false)
   * @returns          Translated string, or empty string if translation fails
   *
   * @example  with capitalize
   *   this.BirthRegisterEntity.p.FirstNameMr =
   *     await this.texttranslationUtil.translate(value, 'en-to-mr', true);
   *
   * @example  without capitalize (notes, addresses, etc.)
   *   this.BirthRegisterEntity.p.NotesMr =
   *     await this.texttranslationUtil.translate(value, 'en-to-mr');
   */
  async translate(
    text: string,
    direction: TranslateLang,
    capitalize: boolean = false
  ): Promise<string> {
    const value = text?.trim();
    if (!value) return "";

    const req = new TranslateTextRequest();
    req.text         = `-${value}-`;
    req.from_language = direction === "en-to-mr" ? 100 : 200;
    req.to_language   = direction === "en-to-mr" ? 200 : 100;

    try {
      const response = await this.communicator.GetTransaltedText(req);

      if (response.Successful) {
        // ✅ Success
        let result = (response.ObjectTag?.ConvertedText ?? "")
          .replace(/^-|-$/g, "")
          .trim();

        if (capitalize && result) {
          result = result.charAt(0).toUpperCase() + result.slice(1);
        }

        return result;
      }

      // ❌ API returned Successful=false — show server's message
      this.showError(response.Message);
      return "";

    } catch {
      // ❌ Network / runtime error — show generic bilingual message
      this.showError();
      return "";
    }
  }

  /**
   * Capitalise the first letter live while the user types (DevExtreme onKeyUp).
   * Call this on English fields — no need for a separate capitalizeFirstLetter() in your component.
   *
   * @example  in HTML
   *   (onKeyUp)="texttranslationUtil.capitalizeOnKeyUp($event, BirthRegisterEntity.p, 'FirstNameEn')"
   */
  capitalizeOnKeyUp<T extends object>(event: any, entity: T, key: keyof T): void {
    const input = event.event?.target as HTMLInputElement | undefined;
    if (!input?.value) return;

    const capitalized = input.value.charAt(0).toUpperCase() + input.value.slice(1);
    if (input.value !== capitalized) {
      const cursor = input.selectionStart;
      input.value  = capitalized;
      input.setSelectionRange(cursor, cursor);
      (entity as any)[key] = capitalized;
    }
  }
}